import React from 'react';
import { useLocalization } from '../../contexts/LocalizationContext';
import useModal from '../../hooks/useModal';
import { useForm } from '../../hooks/useForm';
import { Button, Form, Modal } from 'react-bootstrap';
import Loading from '../Loading';
import TextField from '../forms/fields/TextField';
import SwitchField from '../forms/fields/SwitchField';
import useNotifications from '../../hooks/useNotifications';
import { useAuth } from '../../contexts/AuthContext';

export default function ChangePasswordModal() {
  const { localize } = useLocalization();
  const { notify, clear } = useNotifications();
  const modal = useModal();

  const auth = useAuth();

  const form = useForm({
    oldPassword: '',
    newPassword: '',
    newPasswordAgain: '',
    showPassword: false
  });

  const handleSubmit = async (event) => {
    const isValid = form.validate(event);

    if (form.values.newPassword !== form.values.newPasswordAgain) {
      notify('validation.error', { variant: 'danger' });
      return form.setValidationErrors((prev) =>
        (prev || []).concat({
          path: 'newPasswordAgain',
          type: 'errorMessage',
          message: 'user.passwords.not.match'
        })
      );
    }

    try {
      if (!isValid) return;

      const response = await auth.changePassword({
        oldPassword: form.values.oldPassword,
        newPassword: form.values.newPassword
      });

      if (response) {
        clear();
        notify('user.change.password.success');
        modal.close();
      }
    } catch (error) {
      console.error(error);
      if (Array.isArray(error.errors)) {
        form.setValidationErrors(error.errors);
      }

      if (error.key === 'user.invalid.credentials') {
        console.log('error', error);
        form.setValidationErrors([
          {
            path: 'oldPassword',
            type: 'errorMessage',
            message: 'user.invalid.credentials'
          }
        ]);
      }
    }
  };

  return (
    <Form onSubmit={handleSubmit} validated={form.validated} noValidate>
      <Modal.Header closeButton>
        <Modal.Title>{localize('user.change.password')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {auth.loading && <Loading />}

        <TextField
          required
          className='mb-3'
          disabled={auth.loading}
          type={form.values.showPassword ? 'text' : 'password'}
          label={localize('user.password')}
          errors={form.getValidationErrors('oldPassword')}
          {...form.input('oldPassword')}
        />

        <TextField
          required
          className='mb-3'
          disabled={auth.loading}
          type={form.values.showPassword ? 'text' : 'password'}
          label={localize('user.newPassword')}
          errors={form.getValidationErrors('newPassword')}
          {...form.input('newPassword')}
        />

        <TextField
          required
          className='mb-3'
          disabled={auth.loading}
          type={form.values.showPassword ? 'text' : 'password'}
          label={localize('user.newPasswordAgain')}
          errors={form.getValidationErrors('newPasswordAgain')}
          {...form.input('newPasswordAgain')}
          isValid={
            form.values.newPassword != '' &&
            form.values.newPassword === form.values.newPasswordAgain
          }
        />

        <SwitchField
          className='mb-3'
          label={localize('user.showPassword')}
          disabled={auth.loading}
          {...form.input('showPassword')}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant='secondary'
          onClick={modal.close}
          disabled={auth.loading}>
          {localize('common.cancel')}
        </Button>
        <Button type='submit' disabled={auth.loading}>
          {localize('common.submit')}
        </Button>
      </Modal.Footer>
    </Form>
  );
}
